import { jwtDecode } from "jwt-decode";
import { User, UserManager, WebStorageStateStore } from "oidc-client-ts";

import type { OrganisationClaim } from "shell/platform/types/types";

import { pathsSchema } from "shell/platform/config/paths";
import { STP_REGION_ID, STP_REGIONS } from "shell/platform/constants/constants";
import { getCurrentRegion } from "shell/platform/utils/regionalApolloClientUtils";
import { trimWhitespace } from "shell/platform/utils/utils";

export type AuthProvider = "cognito" | "cloudentity";
export const AUTH_PROVIDER_KEY = "auth-provider";
const STORAGE_LAYER = globalThis.sessionStorage;

export const getStoredAuthProvider = (): AuthProvider | null =>
  STORAGE_LAYER.getItem(AUTH_PROVIDER_KEY) as AuthProvider | null;

export const storeAuthProvider = (provider: AuthProvider): void => {
  STORAGE_LAYER.setItem(AUTH_PROVIDER_KEY, provider);
};

export const clearStoredAuthProvider = (): void => {
  STORAGE_LAYER.removeItem(AUTH_PROVIDER_KEY);
};

type CognitoAuthMode = "hosted" | "password";
export const COGNITO_AUTH_MODE_STORAGE_KEY = "auth-cognito-mode";

export const getCognitoAuthMode = (): CognitoAuthMode =>
  (STORAGE_LAYER.getItem(
    COGNITO_AUTH_MODE_STORAGE_KEY,
  ) as CognitoAuthMode | null) ?? "hosted";

export const storeCognitoAuthMode = (mode: CognitoAuthMode): void => {
  STORAGE_LAYER.setItem(COGNITO_AUTH_MODE_STORAGE_KEY, mode);
};

export const clearCognitoAuthMode = (): void => {
  STORAGE_LAYER.removeItem(COGNITO_AUTH_MODE_STORAGE_KEY);
};

export const isCognitoPasswordMode = () =>
  isCognito() && getCognitoAuthMode() === "password";

export const LOGIN_HINT_KEY = "auth-login-hint";

export const storeLoginHint = (email: string): void => {
  STORAGE_LAYER.setItem(LOGIN_HINT_KEY, email);
};

export const getLoginHint = (): string | null =>
  STORAGE_LAYER.getItem(LOGIN_HINT_KEY);

export const clearLoginHint = (): void => {
  STORAGE_LAYER.removeItem(LOGIN_HINT_KEY);
};

export const isCognito = () => getStoredAuthProvider() === "cognito";

const getRedirectUri = () =>
  isCognito()
    ? `${globalThis.location.origin}${pathsSchema.cognitoCallback}`
    : `${globalThis.location.origin}${pathsSchema.signInRedirect}`;
const LOGOUT_REDIRECT_URI = `${globalThis.location.origin}/signout-callback-oidc`;
const RESPONSE_TYPE = "code";

export const STORAGE_PREFIX = "oidc.user";
export const PMID_STORAGE_KEY = "active-pmid";
export const PMCODE_STORAGE_KEY = "active-pmcode";

const getAuthority = () =>
  isCognito()
    ? (import.meta.env.VITE_COGNITO_AUTHORITY ?? "")
    : import.meta.env.VITE_AUTHORITY;

const getClientId = () =>
  isCognito()
    ? (import.meta.env.VITE_COGNITO_CLIENT_ID ?? "")
    : import.meta.env.VITE_CLIENT_ID;

export const getUser = () => {
  const authority = getAuthority();
  const clientId = getClientId();
  const oidcStorage = STORAGE_LAYER.getItem(
    `${STORAGE_PREFIX}:${authority}:${clientId}`,
  );

  if (!oidcStorage) {
    return null;
  }
  return User.fromStorageString(oidcStorage);
};

export const storePmInfoFromUser = (user: User): void => {
  const pmidInfo = getPmid(user);
  if (!pmidInfo) return;

  if (globalThis.localStorage.getItem(PMID_STORAGE_KEY)) return;

  if (typeof pmidInfo === "object" && "pmid" in pmidInfo) {
    globalThis.localStorage.setItem(
      PMID_STORAGE_KEY,
      JSON.stringify(pmidInfo.pmid.toString()),
    );
    globalThis.localStorage.setItem(
      PMCODE_STORAGE_KEY,
      JSON.stringify(pmidInfo.pmcode),
    );
  } else {
    globalThis.localStorage.setItem(
      PMID_STORAGE_KEY,
      JSON.stringify(pmidInfo.toString()),
    );
  }
};

export const getParsedPmidInfo = () => {
  try {
    const pmidValue = globalThis.localStorage.getItem(PMID_STORAGE_KEY);
    const pmCodeValue = globalThis.localStorage.getItem(PMCODE_STORAGE_KEY);

    return {
      pmid: pmidValue ? JSON.parse(pmidValue) : undefined,
      pmCode: pmCodeValue ? JSON.parse(pmCodeValue) : undefined,
    };
  } catch (_error) {
    return {
      pmid: undefined,
      pmCode: undefined,
    };
  }
};

export const getToken = ({ token_type, access_token }: User) =>
  `${token_type} ${access_token}`;

const getPmidFromAccessToken = (user: User) => {
  const decoded = jwtDecode<{
    multipmid?: Array<{
      pmcode: string;
      pmid: number;
    }>;
    pmid?: number;
  }>(user.access_token);

  if (decoded.multipmid && decoded.multipmid.length > 0) {
    return decoded.multipmid[0];
  }

  return decoded.pmid;
};

const getPmidFromProfile = (user: User) => {
  const profile = user.profile as Record<string, unknown>;

  if (profile.multipmid) {
    try {
      const multi =
        typeof profile.multipmid === "string"
          ? JSON.parse(profile.multipmid)
          : profile.multipmid;
      return multi.length > 0 ? multi[0] : undefined;
    } catch {
      return;
    }
  }

  return profile.pmid;
};

export const getPmid = (user?: User | null) => {
  if (!user) return;

  return isCognito() ? getPmidFromProfile(user) : getPmidFromAccessToken(user);
};

export const getOrganisationsFromAccessToken = (
  accessToken: string,
): OrganisationClaim[] => {
  const decoded = jwtDecode<{ "custom:organisations"?: string }>(accessToken);
  const raw = decoded["custom:organisations"];
  if (!raw) return [];

  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const getUserProfileInfo = (user?: User | null) => {
  if (!user) return null;
  return {
    name: trimWhitespace(
      `${user.profile.given_name ?? ""} ${user.profile.family_name ?? ""}`,
    ),
    email: user.profile.email,
    organizationName:
      typeof (user.profile as Record<string, unknown>)[
        "custom:company_name"
      ] === "string"
        ? ((user.profile as Record<string, unknown>)[
            "custom:company_name"
          ] as string)
        : (() => {
            if (typeof user.profile.company_name === "string") {
              return user.profile.company_name;
            }
            return;
          })(),
  };
};

export const getSessionHeaders = () => {
  const accessTokenOverride = import.meta.env.VITE_ACCESS_TOKEN_OVERRIDE;

  const user = getUser();
  if ((!user || user.expired) && !accessTokenOverride)
    globalThis.location.reload();
  const token = user ? getToken(user) : "";

  const pmidInfo = getParsedPmidInfo();
  const pmid = pmidInfo.pmid ?? getPmid(user);
  const pmCode = pmidInfo.pmCode;

  const isSTPRegion = STP_REGIONS.includes(getCurrentRegion());

  const storedRegion = isSTPRegion
    ? STP_REGION_ID
    : (globalThis.localStorage.getItem("regionData") ?? 0);

  const updatedHeaders: Record<string, string | number> = {
    authorization: accessTokenOverride
      ? `Bearer ${accessTokenOverride}`
      : token,
    Region: storedRegion ?? 0,
  };

  if (pmid) {
    updatedHeaders["x-pmid"] =
      typeof pmid === "object" && "pmid" in pmid ? pmid.pmid : pmid;
  }
  if (pmCode) {
    updatedHeaders["x-pmcode"] = pmCode;
  }

  if (isCognito() && user?.refresh_token) {
    updatedHeaders["x-refresh-token"] = user.refresh_token;
  }

  return updatedHeaders;
};

const getCognitoDomain = () => import.meta.env.VITE_COGNITO_DOMAIN;

const buildUserManager = () => {
  const authority = getAuthority();
  const clientId = getClientId();

  if (!authority || !clientId) {
    throw new Error(
      isCognito()
        ? "No VITE_COGNITO_AUTHORITY or VITE_COGNITO_CLIENT_ID provided"
        : "No VITE_AUTHORITY or VITE_CLIENT_ID provided",
    );
  }

  const baseConfig = {
    userStore: new WebStorageStateStore({
      store: STORAGE_LAYER,
    }),
    authority,
    client_id: clientId,
    redirect_uri: getRedirectUri(),
    silent_redirect_uri: getRedirectUri(),
    response_type: RESPONSE_TYPE,
    loadUserInfo: false,
    automaticSilentRenew: !isCognitoPasswordMode(),
  };

  if (isCognito()) {
    const domain = getCognitoDomain();
    if (!domain) {
      throw new Error("No VITE_COGNITO_DOMAIN provided");
    }

    return new UserManager({
      ...baseConfig,
      scope: "openid profile email",
      metadata: {
        issuer: authority,
        authorization_endpoint: `${domain}/oauth2/authorize`,
        token_endpoint: `${domain}/oauth2/token`,
        userinfo_endpoint: `${domain}/oauth2/userInfo`,
        end_session_endpoint: `${domain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(LOGOUT_REDIRECT_URI)}`,
        jwks_uri: `${authority}/.well-known/jwks.json`,
      },
    });
  }

  return new UserManager({
    ...baseConfig,
    post_logout_redirect_uri: LOGOUT_REDIRECT_URI,
    scope: "openid profile email offline_access",
    revokeTokensOnSignout: true,
  });
};

let cachedUserManager: { key: string; manager: UserManager } | null = null;

/**
 * A UserManager arms its own silent-renew timer the moment it is constructed,
 * so every extra instance is another concurrent refresh-token grant when the
 * access token nears expiry. CloudEntity rotates refresh tokens, which makes
 * those duplicate grants a replay of an already-consumed token.
 *
 * Never construct a UserManager outside this function.
 */
export const getUserManager = () => {
  const key = [
    getStoredAuthProvider(),
    getAuthority(),
    getClientId(),
    getCognitoDomain(),
    getCognitoAuthMode(),
  ].join("|");

  if (cachedUserManager?.key === key) return cachedUserManager.manager;

  const manager = buildUserManager();
  cachedUserManager?.manager.stopSilentRenew();
  cachedUserManager = { key, manager };

  return manager;
};
